/**
 * scopedStorage — Cluster J.A (Feb 2026).
 *
 * Canonical helper for `bayenah.scope.{org_id}.{user_id}.{key}` keys.
 * Previously inlined inside `components/herald/HeraldPanel.jsx`; now
 * extracted so the Onboarding Tour (and any future scoped key) reads
 * through one source of truth. Refactoring the scope shape only has
 * to touch this file.
 *
 * Why scope by both org and user? A laptop shared between two team-
 * mates from different orgs should treat the tour / Herald drafts as
 * independent — keying on user alone would collapse them; keying on
 * org alone would leak per-user UI state across teammates.
 *
 * Fallback values ('noorg' / 'nouser') keep the key well-formed when
 * the caller hasn't fully resolved auth yet — readers should still
 * gate on `user && orgId` before acting on the value, but at least
 * a stray write won't poison a real user/org pair.
 */
export function scopedKey(orgId, userId, suffix) {
  const o = orgId || 'noorg';
  const u = userId || 'nouser';
  return `bayenah.scope.${o}.${u}${suffix ? `.${suffix}` : ''}`;
}

/** Read a scoped boolean flag. Returns `false` on missing/invalid. */
export function readScopedFlag(orgId, userId, suffix) {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(scopedKey(orgId, userId, suffix)) === '1';
  } catch (_e) {
    return false;
  }
}

/** Write a scoped boolean flag. No-op on SSR / quota errors. */
export function writeScopedFlag(orgId, userId, suffix, value) {
  if (typeof window === 'undefined') return;
  try {
    const k = scopedKey(orgId, userId, suffix);
    if (value) window.localStorage.setItem(k, '1');
    else window.localStorage.removeItem(k);
  } catch (_e) {
    // quota / private-mode — silently drop; nothing critical depends on persistence
  }
}

/** Read a scoped JSON value (for richer state). Returns `fallback` on miss/parse error. */
export function readScopedJSON(orgId, userId, suffix, fallback = null) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(scopedKey(orgId, userId, suffix));
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_e) {
    return fallback;
  }
}

/** Write a scoped JSON value. */
export function writeScopedJSON(orgId, userId, suffix, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(scopedKey(orgId, userId, suffix), JSON.stringify(value));
  } catch (_e) { /* drop */ }
}
